import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Package, LogIn } from "lucide-react";
import { PortalDashboard } from "./PortalDashboard";
import { PasswordLoginForm, ChangePassword } from "./PortalPassword";

export const PORTAL_API = `${process.env.REACT_APP_BACKEND_URL}/api/portal`;
// Portal calls use a dedicated axios instance: cookies on, and never the staff JWT header.
export const portalAxios = axios.create({ withCredentials: true });
// company chosen in the header tabs (an e-mail may be mapped to several customers)
portalAxios.interceptors.request.use((cfg) => {
  const cid = sessionStorage.getItem("portal_customer");
  if (cid) cfg.headers["X-Portal-Customer"] = cid;
  return cfg;
});

function AuthCallback({ onDone }) {
  const location = useLocation();
  const navigate = useNavigate();
  const done = useRef(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(location.hash.replace(/^#/, "")).get("session_id");
    (async () => {
      try {
        const r = await portalAxios.post(`${PORTAL_API}/auth/session`, { session_id: sid });
        sessionStorage.setItem("portal_token", r.data.session_token || "");
        window.history.replaceState(null, "", "/portal");
        onDone(r.data);
        navigate("/portal", { replace: true, state: { user: r.data.user } });
      } catch (e) {
        setErr(e.response?.data?.detail || "Sign-in failed");
        window.history.replaceState(null, "", "/portal");
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (err) return <PortalLogin error={err} />;
  return <div className="min-h-screen flex items-center justify-center text-zinc-500 text-sm">Signing you in…</div>;
}

export function PortalLogin({ error, onDone }) {
  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/portal";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <div className="min-h-screen bg-[#0F172A] text-white flex items-center justify-center p-6" data-testid="portal-login">
      <div className="w-full max-w-sm bg-white/5 border border-white/10 rounded-2xl p-8 backdrop-blur">
        <div className="flex items-center gap-2 text-emerald-300 text-xs uppercase tracking-[0.3em]"><Package className="w-4 h-4" /> Customer portal</div>
        <h1 className="text-3xl font-black mt-3">Your orders & shipments</h1>
        <p className="text-sm text-white/70 mt-2">Sign in with the e-mail and password you received from Eurokon, or with your registered Google account. You will only see your own orders, packing lists and loading status.</p>
        {error && <div className="mt-4 text-sm bg-red-500/20 border border-red-400/40 rounded-md p-3" data-testid="portal-login-error">{error}</div>}
        <div className="mt-6"><PasswordLoginForm onDone={onDone || (() => window.location.reload())} /></div>
        <div className="flex items-center gap-3 my-4 text-[11px] text-white/40"><span className="flex-1 border-t border-white/10" />or<span className="flex-1 border-t border-white/10" /></div>
        <button onClick={login} className="w-full h-11 rounded-full border border-white/20 text-white font-semibold flex items-center justify-center gap-2 hover:bg-white/10 transition-colors" data-testid="portal-google-login">
          <LogIn className="w-5 h-5" /> Sign in with Google
        </button>
        <p className="text-[11px] text-white/40 mt-6">Access is by invitation. No login yet? Ask your Eurokon contact for a temporary password — you change it at first sign-in.</p>
      </div>
    </div>
  );
}

export default function PortalApp() {
  const location = useLocation();
  const [auth, setAuth] = useState(null); // null = checking
  const [denied, setDenied] = useState(false);
  const [changing, setChanging] = useState(false);
  useEffect(() => {
    if (location.hash?.includes("session_id=") || auth) return;
    portalAxios.get(`${PORTAL_API}/auth/me`, { headers: sessionStorage.getItem("portal_token") ? { Authorization: `Bearer ${sessionStorage.getItem("portal_token")}` } : {} })
      .then((r) => setAuth(r.data))
      .catch(() => setDenied(true));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (location.hash?.includes("session_id=")) return <AuthCallback onDone={(d) => { setAuth(d); setDenied(false); }} />;
  const logout = () => { setAuth(null); setDenied(true); sessionStorage.removeItem("portal_token"); sessionStorage.removeItem("portal_customer"); };
  const switchCustomer = (c) => { sessionStorage.setItem("portal_customer", c.id); setAuth({ ...auth, customer: c }); };
  if (auth?.customer && auth.must_change_password) return <ChangePassword forced onDone={() => setAuth({ ...auth, must_change_password: false })} />;
  if (auth?.customer && changing) return <ChangePassword onDone={() => setChanging(false)} onCancel={() => setChanging(false)} />;
  if (auth?.customer) return <PortalDashboard key={auth.customer.id} auth={auth} onLogout={logout} onSwitchCustomer={switchCustomer} onChangePassword={auth.has_password ? () => setChanging(true) : null} />;
  if (denied) return <PortalLogin onDone={(d) => { setAuth({ ...d, has_password: true }); setDenied(false); }} />;
  return <div className="min-h-screen flex items-center justify-center text-zinc-500 text-sm">Loading…</div>;
}
